/**
 * Kit add-ons configurator — works with snippets/kit-addons.liquid.
 *
 * Registers a provider with the shared EmbarkAddons layer (assets/cart-addons.js):
 *   - lineItems(): the selected kit step addon lines (each after its native-bundle dropship
 *                  fee line, when a dealer's market source has a fee)
 *   - validate():  every required step answered, and no step left half-chosen
 *   - subtotal():  combined price (cents) of the selections, for the shared total
 *
 * Each addon line carries:
 *   - _kit_parent_sku    SKU of the kit they belong to
 *   - _kit_parent_title  human-readable kit title
 *   - _kit_step          step title the addon came from
 *
 *   - summary():   a "Your Build" line per completed step (the shared layer draws the list)
 *
 * The DOM is the state. Cards are native radios and each card's option
 * dimensions are `.option-selector` radio groups, the same markup the main
 * variant picker uses — so a selection is read back out of the markup rather
 * than mirrored into a JS object. That is also what lets selections survive the
 * combined-listing swap in variant-picker.js, which transfers input state by id.
 *
 * A chosen card whose options don't yet resolve to a variant is `pending`; one
 * that resolves to an unsellable variant is `unavailable`. Both price as 0,
 * submit nothing, and block Add to Cart — on optional steps too, since the cart
 * can't honour a product whose variant was never settled.
 *
 * A card whose product has nothing buyable ([data-kit-unavailable]) answers its step as
 * `noted`: no line, no price, never blocks. Its hidden input rides the main product's line
 * as an "Unavailable Kit Option" property while it is the step's pick.
 */

if (!customElements.get('kit-addons')) {
  const ROVING_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
  const KIT_INPUTS = 'input[data-kit-option], .option-selector input.js-option';
  // Statuses that answer a step.
  const ANSWERED = ['complete', 'noted'];

  /** Variant option values, minus the placeholder Shopify gives single-variant products. */
  function optionLabel(variant) {
    return (variant.options || []).filter((o) => o && o !== 'Default Title').join(' / ');
  }

  class KitAddons extends HTMLElement {
    constructor() {
      super();
      this.provider = null;
      this.skipped = new Set();
      this.arrowNav = false;
      this.variantCache = new WeakMap();
      this.handleChange = this.handleChange.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this.handleChange);
      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('click', this.handleClick);

      this.autoSelectForced();

      this.provider = {
        el: this,
        lineItems: this.getAddonLineItems.bind(this),
        validate: this.validate.bind(this),
        subtotal: this.subtotal.bind(this),
        summary: this.summary.bind(this)
      };
      if (window.EmbarkAddons) window.EmbarkAddons.register(this.provider);

      // Paints from whatever the markup already says, so a swap that carried
      // selections over hydrates without a special case.
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('click', this.handleClick);
      if (window.EmbarkAddons && this.provider) window.EmbarkAddons.unregister(this.provider);
    }

    /* ─── Variant lookup ─── */

    steps() {
      return [...this.querySelectorAll('[data-kit-step]')];
    }

    optionGroups(card) {
      return [...card.querySelectorAll('.option-selector')];
    }

    /** The card's variant table, parsed once. */
    cardVariants(card) {
      if (!this.variantCache.has(card)) {
        const el = card.querySelector('[data-addon-variants]');
        let parsed = [];
        try {
          if (el) parsed = JSON.parse(el.textContent);
        } catch (err) {
          console.error('[kit-addons] invalid variant JSON', err);
        }
        this.variantCache.set(card, Array.isArray(parsed) ? parsed : []);
      }
      return this.variantCache.get(card);
    }

    /** Chosen value per option dimension, in position order. `null` where unanswered. */
    selectedOptionValues(card) {
      return this.optionGroups(card).map((group) => {
        const checked = group.querySelector('input.js-option:checked');
        return checked ? checked.value : null;
      });
    }

    /** The variant matching every chosen value, or null while any dimension is open. */
    resolveVariant(card) {
      const chosen = this.selectedOptionValues(card);
      if (!chosen.length || chosen.some((value) => value === null)) return null;
      return this.cardVariants(card).find(
        (variant) => chosen.every((value, i) => value === variant.options[i])
      ) || null;
    }

    /**
     * A card resolves to exactly one of three states. `unavailable` is reachable
     * because unavailable values stay selectable (the card then reads "Unavailable").
     */
    cardStatus(card) {
      const variant = this.resolveVariant(card);
      if (!variant) return 'pending';
      return variant.sellable ? 'complete' : 'unavailable';
    }

    /* ─── State, read from the DOM ─── */

    /**
     * The single definition of what a step has been answered with. render(),
     * validate(), subtotal() and lineItems() all read it, which is what keeps
     * the `.builder-opt--selected` marker and the gate from ever disagreeing.
     */
    readSelection(stepEl) {
      const stepId = stepEl.dataset.kitStep;
      const stepTitle = stepEl.dataset.stepTitle || stepId;
      const radio = stepEl.querySelector('input[data-kit-option]:checked');

      if (!radio) {
        const status = this.skipped.has(stepId) ? 'skipped' : 'empty';
        return { stepId, stepTitle, status, priceCents: 0 };
      }

      const card = radio.closest('[data-kit-option-card]');
      const name = radio.dataset.optionName || radio.value;
      if (card.hasAttribute('data-kit-unavailable')) {
        return { stepId, stepTitle, card, status: 'noted', priceCents: 0, name };
      }
      const status = this.cardStatus(card);

      if (status !== 'complete') {
        return { stepId, stepTitle, card, status, priceCents: 0, name };
      }

      const variant = this.resolveVariant(card);
      // A dealer's market dropship source adds its fee to the line (EmbarkAddons.lineCents)
      const lineCents = window.EmbarkAddons.lineCents(variant);
      return {
        stepId,
        stepTitle,
        card,
        status,
        name,
        variantId: variant.id,
        variantTitle: optionLabel(variant),
        sku: variant.sku || '',
        priceCents: lineCents,
        priceMoney: variant.dropshipFeeCents > 0 ? window.EmbarkAddons.formatMoney(lineCents) : variant.priceMoney || '',
        image: variant.image || ''
      };
    }

    /**
     * Liquid pre-checks a forced step's sole option before first paint. This is
     * the safety net: variant-picker.js copies `checked` across a product swap
     * by element id, so a forced step can land unchecked.
     */
    autoSelectForced() {
      this.steps().forEach((stepEl) => {
        if (stepEl.dataset.kitForced !== 'true') return;
        if (stepEl.querySelector('input[data-kit-option]:checked')) return;
        const preset = stepEl.querySelector('input[data-kit-default]');
        if (preset) preset.checked = true;
      });
    }

    /**
     * Clear a step. Radios can't be unchecked by clicking, so this is the only
     * path. Option values are left alone on purpose — re-picking the card
     * restores the combination the customer had already settled on.
     */
    clearStep(stepEl) {
      stepEl.querySelectorAll('input[data-kit-option]:checked').forEach((el) => { el.checked = false; });
    }

    /* ─── Provider hooks ─── */

    /** Selected kit step addons as cart line items. */
    getAddonLineItems() {
      const kitTitle = this.dataset.kitTitle || '';
      const kitSku = this.dataset.kitSku || '';
      const items = [];

      this.steps().forEach((stepEl) => {
        const sel = this.readSelection(stepEl);
        if (sel.status !== 'complete') return;
        const numericId = parseInt(String(sel.variantId).replace(/^gid:\/\/shopify\/ProductVariant\//, ''), 10);
        if (!Number.isFinite(numericId)) return;
        const variant = this.resolveVariant(sel.card);
        items.push(...window.EmbarkAddons.dropshipFeeLines(variant, sel.name));
        items.push({
          id: numericId,
          quantity: 1,
          properties: {
            _kit_parent_sku: kitSku,
            _kit_parent_title: kitTitle,
            _kit_step: sel.stepTitle,
            ...window.EmbarkAddons.stockProperties(variant)
          }
        });
      });

      return items;
    }

    /**
     * "Your Build" lines: one per completed step. A forced step has nothing else to
     * choose, so removing it could only ever re-block Add to Cart — no remove().
     */
    summary() {
      return this.steps().flatMap((stepEl) => {
        const sel = this.readSelection(stepEl);
        if (sel.status !== 'complete') return [];
        const line = {
          title: sel.variantTitle ? `${sel.name} — ${sel.variantTitle}` : sel.name,
          image: sel.image,
          priceMoney: sel.priceMoney
        };
        if (stepEl.dataset.kitForced !== 'true') {
          line.remove = () => {
            this.clearStep(stepEl);
            this.render();
          };
        }
        return [line];
      });
    }

    /** Combined price (cents) of the current selections. Unfinished cards contribute 0. */
    subtotal() {
      return this.steps().reduce((cents, stepEl) => cents + (this.readSelection(stepEl).priceCents || 0), 0);
    }

    /**
     * A required step needs a complete selection. An unfinished card blocks as
     * well — including on an optional step — because a started choice the cart
     * can't honour should not be silently dropped.
     */
    validate() {
      const invalid = [];
      let reason = null;

      this.steps().forEach((stepEl) => {
        const sel = this.readSelection(stepEl);
        const unfinished = sel.status === 'pending' || sel.status === 'unavailable';
        const answered = sel.status === 'complete' || sel.status === 'noted';
        const missing = stepEl.dataset.kitRequired === 'true' && !answered;
        if (!unfinished && !missing) return;
        // Steps are walked in DOM order and validateAll() sorts anchors the same
        // way, so the first reason found is the one whose message surfaces.
        if (!reason) reason = unfinished ? sel.status : 'missing';
        invalid.push(stepEl);
      });

      if (!invalid.length) return { valid: true, invalid: [] };

      const messages = {
        pending: 'Please finish choosing options in the highlighted steps.',
        unavailable: 'A kit selection is unavailable. Please select another option.',
        missing: 'Please choose an option for each required step.'
      };
      return { valid: false, invalid, message: messages[reason] };
    }

    /* ─── Event handlers ─── */

    handleChange(e) {
      const target = e.target;
      if (!target.matches(KIT_INPUTS)) return;

      const card = target.closest('[data-kit-option-card]');
      // Choosing an option value chooses its card too — picking "Blue" is
      // picking the product. Same move product-addons.js makes on variant select.
      if (card && !target.matches('input[data-kit-option]')) {
        const radio = card.querySelector('input[data-kit-option]');
        if (radio && !radio.checked) radio.checked = true;
      }

      const stepEl = target.closest('[data-kit-step]');
      if (!stepEl) return;
      this.skipped.delete(stepEl.dataset.kitStep);

      const viaArrowKey = this.arrowNav;
      this.arrowNav = false;

      this.render();
      // Only advance on a finished choice — scrolling away from a card whose
      // options still need settling hides the controls the customer needs next.
      if (!viaArrowKey && ANSWERED.includes(this.readSelection(stepEl).status)) {
        this.scrollToNextStep(stepEl);
      }
    }

    /**
     * Arrow-key roving checks and focuses in one motion, so every arrow press
     * within a radio group fires `change`. Auto-advancing on those would scroll
     * the page out from under a keyboard user; flag them and skip the scroll.
     */
    handleKeydown(e) {
      this.arrowNav = ROVING_KEYS.includes(e.key) && e.target.matches(KIT_INPUTS);
    }

    handleClick(e) {
      const skipEl = e.target.closest('[data-kit-skip]');
      if (skipEl && this.contains(skipEl)) this.handleSkipClick(skipEl);
    }

    handleSkipClick(skipEl) {
      const stepEl = skipEl.closest('[data-kit-step]');
      if (!stepEl) return;
      const stepId = stepEl.dataset.kitStep;
      const justSkipped = !this.skipped.has(stepId);

      this.clearStep(stepEl);
      if (justSkipped) this.skipped.add(stepId);
      else this.skipped.delete(stepId);

      this.render();
      if (justSkipped) this.scrollToNextStep(stepEl);
    }

    /**
     * Advance to the next unanswered step via the shared
     * EmbarkAddons.scrollToAnchor, which skips the scroll when the target is
     * already in view. Past the last step there is nowhere to go — the summary
     * and Add to Cart are already pinned on screen.
     */
    scrollToNextStep(fromStepEl) {
      const steps = this.steps();
      const start = steps.indexOf(fromStepEl);
      if (start === -1) return;
      const target = steps.slice(start + 1).find((el) => {
        const status = this.readSelection(el).status;
        return !ANSWERED.includes(status) && status !== 'skipped';
      });

      if (target && window.EmbarkAddons) window.EmbarkAddons.scrollToAnchor(target);
    }

    /* ─── Render ─── */

    render() {
      this.steps().forEach((stepEl) => {
        const sel = this.readSelection(stepEl);

        stepEl.querySelectorAll('[data-kit-option-card]').forEach((card) => {
          const radio = card.querySelector('input[data-kit-option]');
          const checked = !!(radio && radio.checked);
          const status = checked ? sel.status : null;

          // `.builder-opt--selected` means COMPLETE and carries no styling of its
          // own — it is the marker cart-addons.js fieldAnswered() reads to
          // decide whether this step has been answered. The "chosen" look comes
          // from :has(input:checked) in CSS, so an unfinished card still reads
          // as picked without telling the guided-scroll flow that it is done.
          card.classList.toggle('builder-opt--selected', ANSWERED.includes(status));

          this.renderCard(card);
        });

        const skipBtn = stepEl.querySelector('[data-kit-skip]');
        if (skipBtn) {
          const isSkipped = sel.status === 'skipped';
          skipBtn.classList.toggle('kit-skip--selected', isSkipped);
          skipBtn.setAttribute('aria-pressed', isSkipped ? 'true' : 'false');
          const stateEl = skipBtn.querySelector('[data-kit-skip-state]');
          if (stateEl) stateEl.textContent = isSkipped ? '✓ Skipped' : 'No thanks';
        }
      });

      this.syncUnavailableProps();

      // Shared notify helper, same as the other providers — repaints the Add to Cart total and
      // Your Build.
      if (window.EmbarkAddons) window.EmbarkAddons.notifyChange();
    }

    /**
     * "Unavailable Kit Option" properties on the main product's line: one per picked
     * unavailable card, numbered from the second (a line can't hold two of one name).
     */
    syncUnavailableProps() {
      let n = 0;
      this.querySelectorAll('input[data-unavailable-addon]').forEach((input) => {
        const radio = input.closest('[data-kit-option-card]').querySelector('input[data-kit-option]');
        input.disabled = !radio.checked;
        if (input.disabled) return;
        n += 1;
        input.name = `properties[Unavailable Kit Option${n > 1 ? ` ${n}` : ''}]`;
      });
    }

    /** Paint a card's options, image, price, stock badge and estimate (EmbarkAddons.paintCard). */
    renderCard(card) {
      const groups = this.optionGroups(card);
      if (!groups.length || !window.EmbarkAddons) return;
      window.EmbarkAddons.paintCard(card, {
        groups,
        variants: this.cardVariants(card),
        chosen: this.selectedOptionValues(card),
        variant: this.resolveVariant(card)
      });
    }
  }

  customElements.define('kit-addons', KitAddons);
}
